import { useMutation, useQuery } from '@tanstack/react-query';
import type {
    DataTag,
    DefinedInitialDataOptions,
    DefinedUseQueryResult,
    MutationFunction,
    QueryClient,
    QueryFunction,
    QueryKey,
    UndefinedInitialDataOptions,
    UseMutationOptions,
    UseMutationResult,
    UseQueryOptions,
    UseQueryResult,
} from '@tanstack/react-query';

import { useCallback } from 'react';

import type {
    CreateApiResourceScopeModel,
    CreateOrUpdateApiResourceModel,
    CreateOrUpdateApiResourceRoleModel,
    GetApiResourceByIdParams,
    GetApiResourceByIdResponse,
    GetApiResourceDetailsByIdParams,
    GetApiResourceDetailsByIdResponse,
    GetApiResourceRoleByIdResponse,
    GetApiResourceRoleDetailsByIdParams,
    GetApiResourceRoleDetailsByIdResponse,
    GetApiResourceRoleMembersResponse,
    GetApiResourceRolesByApiResourceIdResponse,
    GetApiResourceRolesParams,
    GetApiResourceScopeByIdResponse,
    GetApiResourceScopeClientsResponse,
    GetApiResourceScopesByApiResourceIdResponse,
    GetApiResourceScopesParams,
    GetApiResourcesParams,
    GetApiResourcesResponse,
    GetTokenExchangeClientsByApiResourceIdResponse,
    GetTokenExchangeClientsParams,
    ProblemDetails,
    UpdateApiResourceScopeModel,
} from '../models';

import { useCustomIDSClient } from '../../custom-client';
import type { ErrorType } from '../../custom-client';

/**
 * Get all API Resources<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.read
 */
export const useGetApiResourcesHook = () => {
    const getApiResources = useCustomIDSClient<GetApiResourcesResponse>();

    return useCallback(
        (params?: GetApiResourcesParams, signal?: AbortSignal) => {
            return getApiResources({ url: `/api-resources`, method: 'GET', params, signal });
        },
        [getApiResources],
    );
};

export const getGetApiResourcesQueryKey = (params?: GetApiResourcesParams) => {
    return [`/api-resources`, ...(params ? [params] : [])] as const;
};

export const useGetApiResourcesQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourcesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetApiResourcesParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourcesHook>>>, TError, TData>>;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetApiResourcesQueryKey(params);

    const getApiResources = useGetApiResourcesHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetApiResourcesHook>>>> = ({ signal }) =>
        getApiResources(params, signal);

    return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApiResourcesHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetApiResourcesQueryResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useGetApiResourcesHook>>>>;
export type GetApiResourcesQueryError = ErrorType<ProblemDetails>;

export function useGetApiResources<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourcesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params: undefined | GetApiResourcesParams,
    options: {
        query: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourcesHook>>>, TError, TData>>
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourcesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourcesHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResources<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourcesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetApiResourcesParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourcesHook>>>, TError, TData>>
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourcesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourcesHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResources<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourcesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetApiResourcesParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourcesHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetApiResources<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourcesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    params?: GetApiResourcesParams,
    options?: {
        query?: Partial<UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourcesHook>>>, TError, TData>>;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetApiResourcesQueryOptions(params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Creates a new API Resource<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.write
 */
export const useCreateApiResourceHook = () => {
    const createApiResource = useCustomIDSClient<GetApiResourceByIdResponse>();

    return useCallback(
        (createOrUpdateApiResourceModel: CreateOrUpdateApiResourceModel, signal?: AbortSignal) => {
            return createApiResource({
                url: `/api-resources`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: createOrUpdateApiResourceModel,
                signal,
            });
        },
        [createApiResource],
    );
};

export const useCreateApiResourceMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateApiResourceHook>>>,
        TError,
        { data: CreateOrUpdateApiResourceModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateApiResourceHook>>>,
    TError,
    { data: CreateOrUpdateApiResourceModel },
    TContext
> => {
    const mutationKey = ['createApiResource'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const createApiResource = useCreateApiResourceHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateApiResourceHook>>>,
        { data: CreateOrUpdateApiResourceModel }
    > = (props) => {
        const { data } = props ?? {};

        return createApiResource(data);
    };

    return { mutationFn, ...mutationOptions };
};

export type CreateApiResourceMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useCreateApiResourceHook>>>
>;
export type CreateApiResourceMutationBody = CreateOrUpdateApiResourceModel;
export type CreateApiResourceMutationError = ErrorType<ProblemDetails>;

export const useCreateApiResource = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateApiResourceHook>>>,
            TError,
            { data: CreateOrUpdateApiResourceModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateApiResourceHook>>>,
    TError,
    { data: CreateOrUpdateApiResourceModel },
    TContext
> => {
    return useMutation(useCreateApiResourceMutationOptions(options), queryClient);
};
/**
 * Returns the basic information of a API Resource<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.read
 */
export const useGetApiResourceByIdHook = () => {
    const getApiResourceById = useCustomIDSClient<GetApiResourceByIdResponse>();

    return useCallback(
        (id: string, params?: GetApiResourceByIdParams, signal?: AbortSignal) => {
            return getApiResourceById({ url: `/api-resources/${id}`, method: 'GET', params, signal });
        },
        [getApiResourceById],
    );
};

export const getGetApiResourceByIdQueryKey = (id: string, params?: GetApiResourceByIdParams) => {
    return [`/api-resources/${id}`, ...(params ? [params] : [])] as const;
};

export const useGetApiResourceByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceByIdHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetApiResourceByIdQueryKey(id, params);

    const getApiResourceById = useGetApiResourceByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetApiResourceByIdHook>>>> = ({ signal }) =>
        getApiResourceById(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApiResourceByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetApiResourceByIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetApiResourceByIdHook>>>
>;
export type GetApiResourceByIdQueryError = ErrorType<ProblemDetails>;

export function useGetApiResourceById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetApiResourceByIdParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceByIdHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceByIdHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetApiResourceById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetApiResourceByIdQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Updates an existing API Resource<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.write
 */
export const useUpdateApiResourceHook = () => {
    const updateApiResource = useCustomIDSClient<GetApiResourceByIdResponse>();

    return useCallback(
        (id: string, createOrUpdateApiResourceModel: CreateOrUpdateApiResourceModel, signal?: AbortSignal) => {
            return updateApiResource({
                url: `/api-resources/${id}`,
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                data: createOrUpdateApiResourceModel,
                signal,
            });
        },
        [updateApiResource],
    );
};

export const useUpdateApiResourceMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateApiResourceHook>>>,
        TError,
        { id: string; data: CreateOrUpdateApiResourceModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateApiResourceHook>>>,
    TError,
    { id: string; data: CreateOrUpdateApiResourceModel },
    TContext
> => {
    const mutationKey = ['updateApiResource'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const updateApiResource = useUpdateApiResourceHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateApiResourceHook>>>,
        { id: string; data: CreateOrUpdateApiResourceModel }
    > = (props) => {
        const { id, data } = props ?? {};

        return updateApiResource(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UpdateApiResourceMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUpdateApiResourceHook>>>
>;
export type UpdateApiResourceMutationBody = CreateOrUpdateApiResourceModel;
export type UpdateApiResourceMutationError = ErrorType<ProblemDetails>;

export const useUpdateApiResource = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateApiResourceHook>>>,
            TError,
            { id: string; data: CreateOrUpdateApiResourceModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateApiResourceHook>>>,
    TError,
    { id: string; data: CreateOrUpdateApiResourceModel },
    TContext
> => {
    return useMutation(useUpdateApiResourceMutationOptions(options), queryClient);
};
/**
 * Deletes an existing API Resource<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.write
 */
export const useDeleteApiResourceHook = () => {
    const deleteApiResource = useCustomIDSClient<void>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return deleteApiResource({ url: `/api-resources/${id}`, method: 'DELETE', signal });
        },
        [deleteApiResource],
    );
};

export const useDeleteApiResourceMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteApiResourceHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteApiResourceHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['deleteApiResource'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deleteApiResource = useDeleteApiResourceHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteApiResourceHook>>>,
        { id: string }
    > = (props) => {
        const { id } = props ?? {};

        return deleteApiResource(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteApiResourceMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeleteApiResourceHook>>>
>;

export type DeleteApiResourceMutationError = ErrorType<ProblemDetails>;

export const useDeleteApiResource = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteApiResourceHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteApiResourceHook>>>,
    TError,
    { id: string },
    TContext
> => {
    return useMutation(useDeleteApiResourceMutationOptions(options), queryClient);
};
/**
 * Returns the details of a API Resource<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.read
 */
export const useGetApiResourceDetailsByIdHook = () => {
    const getApiResourceDetailsById = useCustomIDSClient<GetApiResourceDetailsByIdResponse>();

    return useCallback(
        (id: string, params?: GetApiResourceDetailsByIdParams, signal?: AbortSignal) => {
            return getApiResourceDetailsById({ url: `/api-resources/${id}/detail`, method: 'GET', params, signal });
        },
        [getApiResourceDetailsById],
    );
};

export const getGetApiResourceDetailsByIdQueryKey = (id: string, params?: GetApiResourceDetailsByIdParams) => {
    return [`/api-resources/${id}/detail`, ...(params ? [params] : [])] as const;
};

export const useGetApiResourceDetailsByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceDetailsByIdHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetApiResourceDetailsByIdQueryKey(id, params);

    const getApiResourceDetailsById = useGetApiResourceDetailsByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetApiResourceDetailsByIdHook>>>> = ({
        signal,
    }) => getApiResourceDetailsById(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApiResourceDetailsByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetApiResourceDetailsByIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetApiResourceDetailsByIdHook>>>
>;
export type GetApiResourceDetailsByIdQueryError = ErrorType<ProblemDetails>;

export function useGetApiResourceDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetApiResourceDetailsByIdParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceDetailsByIdHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceDetailsByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceDetailsByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceDetailsByIdHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceDetailsByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceDetailsByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceDetailsByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetApiResourceDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceDetailsByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetApiResourceDetailsByIdQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Moves an API Resource to another application<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.write<br>login.umdaschgroup.com/applications.write
 */
export const useMoveApiResourceHook = () => {
    const moveApiResource = useCustomIDSClient<void>();

    return useCallback(
        (id: string, applicationId: string, signal?: AbortSignal) => {
            return moveApiResource({ url: `/api-resources/${id}/move/${applicationId}`, method: 'POST', signal });
        },
        [moveApiResource],
    );
};

export const useMoveApiResourceMutationOptions = <TError = ErrorType<ProblemDetails>, TContext = unknown>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useMoveApiResourceHook>>>,
        TError,
        { id: string; applicationId: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useMoveApiResourceHook>>>,
    TError,
    { id: string; applicationId: string },
    TContext
> => {
    const mutationKey = ['moveApiResource'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const moveApiResource = useMoveApiResourceHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useMoveApiResourceHook>>>,
        { id: string; applicationId: string }
    > = (props) => {
        const { id, applicationId } = props ?? {};

        return moveApiResource(id, applicationId);
    };

    return { mutationFn, ...mutationOptions };
};

export type MoveApiResourceMutationResult = NonNullable<Awaited<ReturnType<ReturnType<typeof useMoveApiResourceHook>>>>;

export type MoveApiResourceMutationError = ErrorType<ProblemDetails>;

export const useMoveApiResource = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useMoveApiResourceHook>>>,
            TError,
            { id: string; applicationId: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useMoveApiResourceHook>>>,
    TError,
    { id: string; applicationId: string },
    TContext
> => {
    return useMutation(useMoveApiResourceMutationOptions(options), queryClient);
};
/**
 * Get all api resource roles for an api resource<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.read
 */
export const useGetApiResourceRolesHook = () => {
    const getApiResourceRoles = useCustomIDSClient<GetApiResourceRolesByApiResourceIdResponse>();

    return useCallback(
        (id: string, params?: GetApiResourceRolesParams, signal?: AbortSignal) => {
            return getApiResourceRoles({ url: `/api-resources/${id}/roles`, method: 'GET', params, signal });
        },
        [getApiResourceRoles],
    );
};

export const getGetApiResourceRolesQueryKey = (id: string, params?: GetApiResourceRolesParams) => {
    return [`/api-resources/${id}/roles`, ...(params ? [params] : [])] as const;
};

export const useGetApiResourceRolesQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRolesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceRolesParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRolesHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetApiResourceRolesQueryKey(id, params);

    const getApiResourceRoles = useGetApiResourceRolesHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRolesHook>>>> = ({ signal }) =>
        getApiResourceRoles(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApiResourceRolesHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetApiResourceRolesQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRolesHook>>>
>;
export type GetApiResourceRolesQueryError = ErrorType<ProblemDetails>;

export function useGetApiResourceRoles<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRolesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetApiResourceRolesParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRolesHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRolesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRolesHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceRoles<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRolesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceRolesParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRolesHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRolesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRolesHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceRoles<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRolesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceRolesParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRolesHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetApiResourceRoles<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRolesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceRolesParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRolesHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetApiResourceRolesQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Creates a new api resource role<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.write
 */
export const useCreateApiResourceRoleHook = () => {
    const createApiResourceRole = useCustomIDSClient<GetApiResourceRoleByIdResponse>();

    return useCallback(
        (id: string, createOrUpdateApiResourceRoleModel: CreateOrUpdateApiResourceRoleModel, signal?: AbortSignal) => {
            return createApiResourceRole({
                url: `/api-resources/${id}/roles`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: createOrUpdateApiResourceRoleModel,
                signal,
            });
        },
        [createApiResourceRole],
    );
};

export const useCreateApiResourceRoleMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateApiResourceRoleHook>>>,
        TError,
        { id: string; data: CreateOrUpdateApiResourceRoleModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateApiResourceRoleHook>>>,
    TError,
    { id: string; data: CreateOrUpdateApiResourceRoleModel },
    TContext
> => {
    const mutationKey = ['createApiResourceRole'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const createApiResourceRole = useCreateApiResourceRoleHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateApiResourceRoleHook>>>,
        { id: string; data: CreateOrUpdateApiResourceRoleModel }
    > = (props) => {
        const { id, data } = props ?? {};

        return createApiResourceRole(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type CreateApiResourceRoleMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useCreateApiResourceRoleHook>>>
>;
export type CreateApiResourceRoleMutationBody = CreateOrUpdateApiResourceRoleModel;
export type CreateApiResourceRoleMutationError = ErrorType<ProblemDetails>;

export const useCreateApiResourceRole = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateApiResourceRoleHook>>>,
            TError,
            { id: string; data: CreateOrUpdateApiResourceRoleModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateApiResourceRoleHook>>>,
    TError,
    { id: string; data: CreateOrUpdateApiResourceRoleModel },
    TContext
> => {
    return useMutation(useCreateApiResourceRoleMutationOptions(options), queryClient);
};
/**
 * Get all API resource scopes for an api resource<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.read
 */
export const useGetApiResourceScopesHook = () => {
    const getApiResourceScopes = useCustomIDSClient<GetApiResourceScopesByApiResourceIdResponse>();

    return useCallback(
        (id: string, params?: GetApiResourceScopesParams, signal?: AbortSignal) => {
            return getApiResourceScopes({ url: `/api-resources/${id}/scopes`, method: 'GET', params, signal });
        },
        [getApiResourceScopes],
    );
};

export const getGetApiResourceScopesQueryKey = (id: string, params?: GetApiResourceScopesParams) => {
    return [`/api-resources/${id}/scopes`, ...(params ? [params] : [])] as const;
};

export const useGetApiResourceScopesQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceScopesParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopesHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetApiResourceScopesQueryKey(id, params);

    const getApiResourceScopes = useGetApiResourceScopesHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopesHook>>>> = ({ signal }) =>
        getApiResourceScopes(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopesHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetApiResourceScopesQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopesHook>>>
>;
export type GetApiResourceScopesQueryError = ErrorType<ProblemDetails>;

export function useGetApiResourceScopes<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetApiResourceScopesParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopesHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopesHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceScopes<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceScopesParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopesHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopesHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopesHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceScopes<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceScopesParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopesHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetApiResourceScopes<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopesHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceScopesParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopesHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetApiResourceScopesQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Creates a new api resource scope<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.write
 */
export const useCreateApiResourceScopeHook = () => {
    const createApiResourceScope = useCustomIDSClient<GetApiResourceScopeByIdResponse>();

    return useCallback(
        (id: string, createApiResourceScopeModel: CreateApiResourceScopeModel, signal?: AbortSignal) => {
            return createApiResourceScope({
                url: `/api-resources/${id}/scopes`,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: createApiResourceScopeModel,
                signal,
            });
        },
        [createApiResourceScope],
    );
};

export const useCreateApiResourceScopeMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useCreateApiResourceScopeHook>>>,
        TError,
        { id: string; data: CreateApiResourceScopeModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useCreateApiResourceScopeHook>>>,
    TError,
    { id: string; data: CreateApiResourceScopeModel },
    TContext
> => {
    const mutationKey = ['createApiResourceScope'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const createApiResourceScope = useCreateApiResourceScopeHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useCreateApiResourceScopeHook>>>,
        { id: string; data: CreateApiResourceScopeModel }
    > = (props) => {
        const { id, data } = props ?? {};

        return createApiResourceScope(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type CreateApiResourceScopeMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useCreateApiResourceScopeHook>>>
>;
export type CreateApiResourceScopeMutationBody = CreateApiResourceScopeModel;
export type CreateApiResourceScopeMutationError = ErrorType<ProblemDetails>;

export const useCreateApiResourceScope = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useCreateApiResourceScopeHook>>>,
            TError,
            { id: string; data: CreateApiResourceScopeModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useCreateApiResourceScopeHook>>>,
    TError,
    { id: string; data: CreateApiResourceScopeModel },
    TContext
> => {
    return useMutation(useCreateApiResourceScopeMutationOptions(options), queryClient);
};
/**
 * Get all token exchange clients for an API Resource<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.read<br>login.umdaschgroup.com/clients.read
 */
export const useGetTokenExchangeClientsHook = () => {
    const getTokenExchangeClients = useCustomIDSClient<GetTokenExchangeClientsByApiResourceIdResponse>();

    return useCallback(
        (id: string, params?: GetTokenExchangeClientsParams, signal?: AbortSignal) => {
            return getTokenExchangeClients({
                url: `/api-resources/${id}/token-exchange`,
                method: 'GET',
                params,
                signal,
            });
        },
        [getTokenExchangeClients],
    );
};

export const getGetTokenExchangeClientsQueryKey = (id: string, params?: GetTokenExchangeClientsParams) => {
    return [`/api-resources/${id}/token-exchange`, ...(params ? [params] : [])] as const;
};

export const useGetTokenExchangeClientsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetTokenExchangeClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetTokenExchangeClientsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTokenExchangeClientsHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetTokenExchangeClientsQueryKey(id, params);

    const getTokenExchangeClients = useGetTokenExchangeClientsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetTokenExchangeClientsHook>>>> = ({
        signal,
    }) => getTokenExchangeClients(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetTokenExchangeClientsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetTokenExchangeClientsQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetTokenExchangeClientsHook>>>
>;
export type GetTokenExchangeClientsQueryError = ErrorType<ProblemDetails>;

export function useGetTokenExchangeClients<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTokenExchangeClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetTokenExchangeClientsParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTokenExchangeClientsHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetTokenExchangeClientsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetTokenExchangeClientsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTokenExchangeClients<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTokenExchangeClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetTokenExchangeClientsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTokenExchangeClientsHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetTokenExchangeClientsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetTokenExchangeClientsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetTokenExchangeClients<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTokenExchangeClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetTokenExchangeClientsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTokenExchangeClientsHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetTokenExchangeClients<
    TData = Awaited<ReturnType<ReturnType<typeof useGetTokenExchangeClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetTokenExchangeClientsParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetTokenExchangeClientsHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetTokenExchangeClientsQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Adds a token exchange client to the API Resource<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.write<br>login.umdaschgroup.com/clients.read
 */
export const useAddTokenExchangeClientHook = () => {
    const addTokenExchangeClient = useCustomIDSClient<void>();

    return useCallback(
        (id: string, clientid: string, signal?: AbortSignal) => {
            return addTokenExchangeClient({
                url: `/api-resources/${id}/token-exchange/${clientid}`,
                method: 'PUT',
                signal,
            });
        },
        [addTokenExchangeClient],
    );
};

export const useAddTokenExchangeClientMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useAddTokenExchangeClientHook>>>,
        TError,
        { id: string; clientid: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useAddTokenExchangeClientHook>>>,
    TError,
    { id: string; clientid: string },
    TContext
> => {
    const mutationKey = ['addTokenExchangeClient'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const addTokenExchangeClient = useAddTokenExchangeClientHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useAddTokenExchangeClientHook>>>,
        { id: string; clientid: string }
    > = (props) => {
        const { id, clientid } = props ?? {};

        return addTokenExchangeClient(id, clientid);
    };

    return { mutationFn, ...mutationOptions };
};

export type AddTokenExchangeClientMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useAddTokenExchangeClientHook>>>
>;

export type AddTokenExchangeClientMutationError = ErrorType<ProblemDetails>;

export const useAddTokenExchangeClient = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useAddTokenExchangeClientHook>>>,
            TError,
            { id: string; clientid: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useAddTokenExchangeClientHook>>>,
    TError,
    { id: string; clientid: string },
    TContext
> => {
    return useMutation(useAddTokenExchangeClientMutationOptions(options), queryClient);
};
/**
 * Removes a token exchange client from the API Resource<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.write<br>login.umdaschgroup.com/clients.read
 */
export const useRemoveTokenExchangeClientHook = () => {
    const removeTokenExchangeClient = useCustomIDSClient<void>();

    return useCallback(
        (id: string, clientid: string, signal?: AbortSignal) => {
            return removeTokenExchangeClient({
                url: `/api-resources/${id}/token-exchange/${clientid}`,
                method: 'DELETE',
                signal,
            });
        },
        [removeTokenExchangeClient],
    );
};

export const useRemoveTokenExchangeClientMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useRemoveTokenExchangeClientHook>>>,
        TError,
        { id: string; clientid: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useRemoveTokenExchangeClientHook>>>,
    TError,
    { id: string; clientid: string },
    TContext
> => {
    const mutationKey = ['removeTokenExchangeClient'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const removeTokenExchangeClient = useRemoveTokenExchangeClientHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useRemoveTokenExchangeClientHook>>>,
        { id: string; clientid: string }
    > = (props) => {
        const { id, clientid } = props ?? {};

        return removeTokenExchangeClient(id, clientid);
    };

    return { mutationFn, ...mutationOptions };
};

export type RemoveTokenExchangeClientMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useRemoveTokenExchangeClientHook>>>
>;

export type RemoveTokenExchangeClientMutationError = ErrorType<ProblemDetails>;

export const useRemoveTokenExchangeClient = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useRemoveTokenExchangeClientHook>>>,
            TError,
            { id: string; clientid: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useRemoveTokenExchangeClientHook>>>,
    TError,
    { id: string; clientid: string },
    TContext
> => {
    return useMutation(useRemoveTokenExchangeClientMutationOptions(options), queryClient);
};
/**
 * Returns the basic information of an api resource role<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.read
 */
export const useGetApiResourceRoleByIdHook = () => {
    const getApiResourceRoleById = useCustomIDSClient<GetApiResourceRoleByIdResponse>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return getApiResourceRoleById({ url: `/api-resources/roles/${id}`, method: 'GET', signal });
        },
        [getApiResourceRoleById],
    );
};

export const getGetApiResourceRoleByIdQueryKey = (id: string) => {
    return [`/api-resources/roles/${id}`] as const;
};

export const useGetApiResourceRoleByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleByIdHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetApiResourceRoleByIdQueryKey(id);

    const getApiResourceRoleById = useGetApiResourceRoleByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleByIdHook>>>> = ({
        signal,
    }) => getApiResourceRoleById(id, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetApiResourceRoleByIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleByIdHook>>>
>;
export type GetApiResourceRoleByIdQueryError = ErrorType<ProblemDetails>;

export function useGetApiResourceRoleById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleByIdHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceRoleById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleByIdHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceRoleById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetApiResourceRoleById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetApiResourceRoleByIdQueryOptions(id, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Updates an existing api resource role<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.write
 */
export const useUpdateApiResourceRoleHook = () => {
    const updateApiResourceRole = useCustomIDSClient<GetApiResourceRoleByIdResponse>();

    return useCallback(
        (id: string, createOrUpdateApiResourceRoleModel: CreateOrUpdateApiResourceRoleModel, signal?: AbortSignal) => {
            return updateApiResourceRole({
                url: `/api-resources/roles/${id}`,
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                data: createOrUpdateApiResourceRoleModel,
                signal,
            });
        },
        [updateApiResourceRole],
    );
};

export const useUpdateApiResourceRoleMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateApiResourceRoleHook>>>,
        TError,
        { id: string; data: CreateOrUpdateApiResourceRoleModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateApiResourceRoleHook>>>,
    TError,
    { id: string; data: CreateOrUpdateApiResourceRoleModel },
    TContext
> => {
    const mutationKey = ['updateApiResourceRole'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const updateApiResourceRole = useUpdateApiResourceRoleHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateApiResourceRoleHook>>>,
        { id: string; data: CreateOrUpdateApiResourceRoleModel }
    > = (props) => {
        const { id, data } = props ?? {};

        return updateApiResourceRole(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UpdateApiResourceRoleMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUpdateApiResourceRoleHook>>>
>;
export type UpdateApiResourceRoleMutationBody = CreateOrUpdateApiResourceRoleModel;
export type UpdateApiResourceRoleMutationError = ErrorType<ProblemDetails>;

export const useUpdateApiResourceRole = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateApiResourceRoleHook>>>,
            TError,
            { id: string; data: CreateOrUpdateApiResourceRoleModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateApiResourceRoleHook>>>,
    TError,
    { id: string; data: CreateOrUpdateApiResourceRoleModel },
    TContext
> => {
    return useMutation(useUpdateApiResourceRoleMutationOptions(options), queryClient);
};
/**
 * Deletes an existing api Resource<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.write
 */
export const useDeleteApiResourceRoleHook = () => {
    const deleteApiResourceRole = useCustomIDSClient<void>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return deleteApiResourceRole({ url: `/api-resources/roles/${id}`, method: 'DELETE', signal });
        },
        [deleteApiResourceRole],
    );
};

export const useDeleteApiResourceRoleMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteApiResourceRoleHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteApiResourceRoleHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['deleteApiResourceRole'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deleteApiResourceRole = useDeleteApiResourceRoleHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteApiResourceRoleHook>>>,
        { id: string }
    > = (props) => {
        const { id } = props ?? {};

        return deleteApiResourceRole(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteApiResourceRoleMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeleteApiResourceRoleHook>>>
>;

export type DeleteApiResourceRoleMutationError = ErrorType<ProblemDetails>;

export const useDeleteApiResourceRole = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteApiResourceRoleHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteApiResourceRoleHook>>>,
    TError,
    { id: string },
    TContext
> => {
    return useMutation(useDeleteApiResourceRoleMutationOptions(options), queryClient);
};
/**
 * Returns the details of an api resource role<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.read
 */
export const useGetApiResourceRoleDetailsByIdHook = () => {
    const getApiResourceRoleDetailsById = useCustomIDSClient<GetApiResourceRoleDetailsByIdResponse>();

    return useCallback(
        (id: string, params?: GetApiResourceRoleDetailsByIdParams, signal?: AbortSignal) => {
            return getApiResourceRoleDetailsById({
                url: `/api-resources/roles/${id}/detail`,
                method: 'GET',
                params,
                signal,
            });
        },
        [getApiResourceRoleDetailsById],
    );
};

export const getGetApiResourceRoleDetailsByIdQueryKey = (id: string, params?: GetApiResourceRoleDetailsByIdParams) => {
    return [`/api-resources/roles/${id}/detail`, ...(params ? [params] : [])] as const;
};

export const useGetApiResourceRoleDetailsByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceRoleDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleDetailsByIdHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetApiResourceRoleDetailsByIdQueryKey(id, params);

    const getApiResourceRoleDetailsById = useGetApiResourceRoleDetailsByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleDetailsByIdHook>>>> = ({
        signal,
    }) => getApiResourceRoleDetailsById(id, params, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleDetailsByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetApiResourceRoleDetailsByIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleDetailsByIdHook>>>
>;
export type GetApiResourceRoleDetailsByIdQueryError = ErrorType<ProblemDetails>;

export function useGetApiResourceRoleDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params: undefined | GetApiResourceRoleDetailsByIdParams,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleDetailsByIdHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleDetailsByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleDetailsByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceRoleDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceRoleDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleDetailsByIdHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleDetailsByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleDetailsByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceRoleDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceRoleDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleDetailsByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetApiResourceRoleDetailsById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleDetailsByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    params?: GetApiResourceRoleDetailsByIdParams,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleDetailsByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetApiResourceRoleDetailsByIdQueryOptions(id, params, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Get all members of an api resource role<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.read
 */
export const useGetApiResourceRoleMembersHook = () => {
    const getApiResourceRoleMembers = useCustomIDSClient<GetApiResourceRoleMembersResponse>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return getApiResourceRoleMembers({ url: `/api-resources/roles/${id}/members`, method: 'GET', signal });
        },
        [getApiResourceRoleMembers],
    );
};

export const getGetApiResourceRoleMembersQueryKey = (id: string) => {
    return [`/api-resources/roles/${id}/members`] as const;
};

export const useGetApiResourceRoleMembersQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleMembersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleMembersHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetApiResourceRoleMembersQueryKey(id);

    const getApiResourceRoleMembers = useGetApiResourceRoleMembersHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleMembersHook>>>> = ({
        signal,
    }) => getApiResourceRoleMembers(id, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleMembersHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetApiResourceRoleMembersQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleMembersHook>>>
>;
export type GetApiResourceRoleMembersQueryError = ErrorType<ProblemDetails>;

export function useGetApiResourceRoleMembers<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleMembersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleMembersHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleMembersHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleMembersHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceRoleMembers<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleMembersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleMembersHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleMembersHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleMembersHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceRoleMembers<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleMembersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleMembersHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetApiResourceRoleMembers<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleMembersHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceRoleMembersHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetApiResourceRoleMembersQueryOptions(id, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Returns the basic information of an api resource scope<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.read
 */
export const useGetApiResourceScopeByIdHook = () => {
    const getApiResourceScopeById = useCustomIDSClient<GetApiResourceScopeByIdResponse>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return getApiResourceScopeById({ url: `/api-resources/scopes/${id}`, method: 'GET', signal });
        },
        [getApiResourceScopeById],
    );
};

export const getGetApiResourceScopeByIdQueryKey = (id: string) => {
    return [`/api-resources/scopes/${id}`] as const;
};

export const useGetApiResourceScopeByIdQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeByIdHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetApiResourceScopeByIdQueryKey(id);

    const getApiResourceScopeById = useGetApiResourceScopeByIdHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeByIdHook>>>> = ({
        signal,
    }) => getApiResourceScopeById(id, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeByIdHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetApiResourceScopeByIdQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeByIdHook>>>
>;
export type GetApiResourceScopeByIdQueryError = ErrorType<ProblemDetails>;

export function useGetApiResourceScopeById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeByIdHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceScopeById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeByIdHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeByIdHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeByIdHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceScopeById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetApiResourceScopeById<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeByIdHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeByIdHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetApiResourceScopeByIdQueryOptions(id, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}

/**
 * Updates an existing api resource scope<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.write
 */
export const useUpdateApiResourceScopeHook = () => {
    const updateApiResourceScope = useCustomIDSClient<GetApiResourceScopeByIdResponse>();

    return useCallback(
        (id: string, updateApiResourceScopeModel: UpdateApiResourceScopeModel, signal?: AbortSignal) => {
            return updateApiResourceScope({
                url: `/api-resources/scopes/${id}`,
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                data: updateApiResourceScopeModel,
                signal,
            });
        },
        [updateApiResourceScope],
    );
};

export const useUpdateApiResourceScopeMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useUpdateApiResourceScopeHook>>>,
        TError,
        { id: string; data: UpdateApiResourceScopeModel },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useUpdateApiResourceScopeHook>>>,
    TError,
    { id: string; data: UpdateApiResourceScopeModel },
    TContext
> => {
    const mutationKey = ['updateApiResourceScope'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const updateApiResourceScope = useUpdateApiResourceScopeHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useUpdateApiResourceScopeHook>>>,
        { id: string; data: UpdateApiResourceScopeModel }
    > = (props) => {
        const { id, data } = props ?? {};

        return updateApiResourceScope(id, data);
    };

    return { mutationFn, ...mutationOptions };
};

export type UpdateApiResourceScopeMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useUpdateApiResourceScopeHook>>>
>;
export type UpdateApiResourceScopeMutationBody = UpdateApiResourceScopeModel;
export type UpdateApiResourceScopeMutationError = ErrorType<ProblemDetails>;

export const useUpdateApiResourceScope = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useUpdateApiResourceScopeHook>>>,
            TError,
            { id: string; data: UpdateApiResourceScopeModel },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useUpdateApiResourceScopeHook>>>,
    TError,
    { id: string; data: UpdateApiResourceScopeModel },
    TContext
> => {
    return useMutation(useUpdateApiResourceScopeMutationOptions(options), queryClient);
};
/**
 * Deletes an existing API Resource<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.write
 */
export const useDeleteApiResourceScopeHook = () => {
    const deleteApiResourceScope = useCustomIDSClient<void>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return deleteApiResourceScope({ url: `/api-resources/scopes/${id}`, method: 'DELETE', signal });
        },
        [deleteApiResourceScope],
    );
};

export const useDeleteApiResourceScopeMutationOptions = <
    TError = ErrorType<ProblemDetails>,
    TContext = unknown,
>(options?: {
    mutation?: UseMutationOptions<
        Awaited<ReturnType<ReturnType<typeof useDeleteApiResourceScopeHook>>>,
        TError,
        { id: string },
        TContext
    >;
}): UseMutationOptions<
    Awaited<ReturnType<ReturnType<typeof useDeleteApiResourceScopeHook>>>,
    TError,
    { id: string },
    TContext
> => {
    const mutationKey = ['deleteApiResourceScope'];
    const { mutation: mutationOptions } = options
        ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
            ? options
            : { ...options, mutation: { ...options.mutation, mutationKey } }
        : { mutation: { mutationKey } };

    const deleteApiResourceScope = useDeleteApiResourceScopeHook();

    const mutationFn: MutationFunction<
        Awaited<ReturnType<ReturnType<typeof useDeleteApiResourceScopeHook>>>,
        { id: string }
    > = (props) => {
        const { id } = props ?? {};

        return deleteApiResourceScope(id);
    };

    return { mutationFn, ...mutationOptions };
};

export type DeleteApiResourceScopeMutationResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useDeleteApiResourceScopeHook>>>
>;

export type DeleteApiResourceScopeMutationError = ErrorType<ProblemDetails>;

export const useDeleteApiResourceScope = <TError = ErrorType<ProblemDetails>, TContext = unknown>(
    options?: {
        mutation?: UseMutationOptions<
            Awaited<ReturnType<ReturnType<typeof useDeleteApiResourceScopeHook>>>,
            TError,
            { id: string },
            TContext
        >;
    },
    queryClient?: QueryClient,
): UseMutationResult<
    Awaited<ReturnType<ReturnType<typeof useDeleteApiResourceScopeHook>>>,
    TError,
    { id: string },
    TContext
> => {
    return useMutation(useDeleteApiResourceScopeMutationOptions(options), queryClient);
};
/**
 * Get all members of an api resource scope<br><br><b>Required Scopes</b><br>login.umdaschgroup.com/apiresources.read
 */
export const useGetApiResourceScopeClientsHook = () => {
    const getApiResourceScopeClients = useCustomIDSClient<GetApiResourceScopeClientsResponse>();

    return useCallback(
        (id: string, signal?: AbortSignal) => {
            return getApiResourceScopeClients({ url: `/api-resources/scopes/${id}/clients`, method: 'GET', signal });
        },
        [getApiResourceScopeClients],
    );
};

export const getGetApiResourceScopeClientsQueryKey = (id: string) => {
    return [`/api-resources/scopes/${id}/clients`] as const;
};

export const useGetApiResourceScopeClientsQueryOptions = <
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeClientsHook>>>, TError, TData>
        >;
    },
) => {
    const { query: queryOptions } = options ?? {};

    const queryKey = queryOptions?.queryKey ?? getGetApiResourceScopeClientsQueryKey(id);

    const getApiResourceScopeClients = useGetApiResourceScopeClientsHook();

    const queryFn: QueryFunction<Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeClientsHook>>>> = ({
        signal,
    }) => getApiResourceScopeClients(id, signal);

    return { queryKey, queryFn, enabled: !!id, ...queryOptions } as UseQueryOptions<
        Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeClientsHook>>>,
        TError,
        TData
    > & { queryKey: DataTag<QueryKey, TData, TError> };
};

export type GetApiResourceScopeClientsQueryResult = NonNullable<
    Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeClientsHook>>>
>;
export type GetApiResourceScopeClientsQueryError = ErrorType<ProblemDetails>;

export function useGetApiResourceScopeClients<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options: {
        query: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeClientsHook>>>, TError, TData>
        >
            & Pick<
                DefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeClientsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeClientsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceScopeClients<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeClientsHook>>>, TError, TData>
        >
            & Pick<
                UndefinedInitialDataOptions<
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeClientsHook>>>,
                    TError,
                    Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeClientsHook>>>
                >,
                'initialData'
            >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };
export function useGetApiResourceScopeClients<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeClientsHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> };

export function useGetApiResourceScopeClients<
    TData = Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeClientsHook>>>,
    TError = ErrorType<ProblemDetails>,
>(
    id: string,
    options?: {
        query?: Partial<
            UseQueryOptions<Awaited<ReturnType<ReturnType<typeof useGetApiResourceScopeClientsHook>>>, TError, TData>
        >;
    },
    queryClient?: QueryClient,
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
    const queryOptions = useGetApiResourceScopeClientsQueryOptions(id, options);

    const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
        queryKey: DataTag<QueryKey, TData, TError>;
    };

    return { ...query, queryKey: queryOptions.queryKey };
}
